import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { poolAddress } from "../api/types";
import type { ChainId, Token } from "../api/types";
import { TokenEditor } from "./TokenEditor";

type Props = {
  chain: ChainId;
  /** Selected token id, or empty string when nothing chosen yet. */
  value: string;
  onChange: (tokenId: string) => void;
  tokens: Token[];
  onCreate: (body: unknown) => Promise<Token>;
  /** When true, disables changing selection (used in TaskEditor edit mode). */
  disabled?: boolean;
};

export function TokenPicker({
  chain,
  value,
  onChange,
  tokens,
  onCreate,
  disabled = false,
}: Props): JSX.Element {
  const [showCreate, setShowCreate] = useState(false);

  const filtered = useMemo(
    () => tokens.filter((t) => t.chain === chain && (!t.archived || t.id === value)),
    [tokens, chain, value],
  );
  const selected = useMemo(
    () => tokens.find((t) => t.id === value) ?? null,
    [tokens, value],
  );

  return (
    <div>
      <div className="flex gap-2">
        <select
          className="input flex-1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          title={disabled ? "Token is locked after task creation" : undefined}
        >
          <option value="">— select token —</option>
          {filtered.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} — {t.mint.slice(0, 8)}…{t.mint.slice(-4)} — {t.pools.length}{" "}
              pool{t.pools.length === 1 ? "" : "s"}
            </option>
          ))}
        </select>
        {!disabled && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="btn btn-ghost"
            title="Add a new token"
          >
            <Plus size={14} strokeWidth={1.5} />
            New token
          </button>
        )}
      </div>

      {selected && (
        <div className="mt-3 border border-border bg-hover px-3 py-2 text-xs">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-muted uppercase tracking-nav text-[10px]">Mint</span>
            <span className="font-mono">{selected.mint}</span>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-muted uppercase tracking-nav text-[10px] mt-0.5">
              Pools ({selected.pools.length})
            </span>
            <ul className="font-mono text-[11px] space-y-0.5 flex-1">
              {selected.pools.map((p) => {
                const addr = poolAddress(p);
                return (
                  <li key={addr}>
                    <span className="text-muted">{p.dex}</span>{" "}
                    <span>
                      {addr.slice(0, 8)}…{addr.slice(-4)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {filtered.length === 0 && !disabled && (
        <div className="mt-2 text-xs text-muted">
          No tokens on {chain} yet — click <span className="text-fg">New token</span> to
          add one.
        </div>
      )}

      {showCreate && (
        <TokenEditor
          mode="create"
          onCancel={() => setShowCreate(false)}
          onSubmit={async (body) => {
            const created = await onCreate(body);
            setShowCreate(false);
            onChange(created.id);
          }}
        />
      )}
    </div>
  );
}
